import {
  Gavel,
  Car,
  HeartPulse,
  Truck,
  Footprints,
  Award,
  Building2,
  Scale,
  ShieldCheck,
  MapPin,
  Star,
  Landmark,
} from "lucide-react";

export const FIRM = {
  name: "Mahaney Law",
  attorney: "Joseph M. Mahaney",
  tagline: "40+ Years of Experience",
  promise: "When Results Matter",
  serviceArea: "Serving All of Massachusetts",
  phone: "(508) 499-1778",
  phoneHref: "+15084991778",
  fax: "(508) 561-6856",
  email: "jmahaney@jmahaneylaw.com",
  address: {
    line1: "932 Concord St",
    line2: "Framingham, MA 01701",
  },
  hours: "Mon – Fri  •  8:30 AM – 6:00 PM",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Attorney", to: "/about" },
  { label: "Practice Areas", to: "/practice-areas" },
  { label: "Results", to: "/results" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Contact", to: "/contact" },
];

export const PRACTICE_AREAS = [
  {
    slug: "personal-injury",
    title: "Personal Injury",
    icon: HeartPulse,
    short: "Recovering full and fair compensation for clients hurt by negligence.",
    description:
      "When negligence causes serious injury, the consequences reach every corner of your life — medical bills, lost income, and lasting pain. Attorney Joseph M. Mahaney brings 40+ years of trial experience to building methodical, evidence-driven cases that hold insurance companies and at-fault parties fully accountable.",
    caseTypes: [
      "Catastrophic & traumatic injury",
      "Premises liability",
      "Wrongful death claims",
      "Dog bites & animal attacks",
      "Defective & dangerous products",
      "Negligent security",
    ],
    faqs: [
      {
        q: "How much does it cost to hire a personal injury lawyer?",
        a: "Most personal injury cases are handled on a contingency fee basis — you pay no attorney's fee unless we recover compensation for you. Your initial consultation is always free.",
      },
      {
        q: "How long do I have to file a personal injury claim in Massachusetts?",
        a: "Massachusetts generally allows three years from the date of injury to file a personal injury lawsuit. Some claims carry shorter deadlines, so it is important to speak with an attorney as soon as possible.",
      },
      {
        q: "What is my personal injury case worth?",
        a: "Value depends on the severity of your injuries, medical costs, lost wages, and the long-term impact on your life. After reviewing your case, Attorney Mahaney can give you an honest assessment.",
      },
    ],
    metaTitle: "Massachusetts Personal Injury Attorney | Mahaney Law",
    metaDescription:
      "Injured by negligence in Massachusetts? Attorney Joseph M. Mahaney offers 40+ years of trial experience fighting for full compensation. Free consultation.",
  },
  {
    slug: "motor-vehicle-accidents",
    title: "Motor Vehicle Accidents",
    icon: Car,
    short: "Serious crash cases handled with forensic precision.",
    description:
      "Car, motorcycle, and pedestrian collisions can leave victims facing mounting bills and uncooperative insurers. Attorney Mahaney pursues maximum recovery through aggressive negotiation — and trial when insurers refuse to pay fair value.",
    caseTypes: [
      "Car & SUV collisions",
      "Motorcycle accidents",
      "Pedestrian & bicycle accidents",
      "Rear-end & intersection crashes",
      "Hit-and-run & uninsured motorist claims",
      "Rideshare (Uber / Lyft) accidents",
    ],
    faqs: [
      {
        q: "What should I do after a car accident in Massachusetts?",
        a: "Seek medical attention, document the scene, report the crash, and avoid giving recorded statements to insurers before speaking with an attorney. Early evidence preservation can make or break your claim.",
      },
      {
        q: "The insurance company offered me a settlement — should I accept it?",
        a: "Initial offers are frequently far below the true value of a claim. Have an experienced attorney review any offer before you sign or cash a check, which can waive your rights.",
      },
      {
        q: "What if the other driver was uninsured?",
        a: "Massachusetts requires uninsured/underinsured motorist coverage. We help you pursue recovery through your own policy when the at-fault driver lacks adequate insurance.",
      },
    ],
    metaTitle: "Massachusetts Car Accident Lawyer | Mahaney Law",
    metaDescription:
      "Hurt in a Massachusetts car or motorcycle accident? Attorney Joseph M. Mahaney fights insurers for maximum recovery. Free consultation, no fee unless you win.",
  },
  {
    slug: "truck-accidents",
    title: "Truck Accidents",
    icon: Truck,
    short: "Holding trucking companies and their insurers fully accountable.",
    description:
      "Truck and commercial vehicle crashes cause some of the most catastrophic injuries on Massachusetts roads. These cases involve federal trucking regulations, multiple liable parties, and well-funded insurers. Attorney Mahaney brings 40+ years of trial experience to hold trucking companies and their insurers accountable.",
    caseTypes: [
      "Tractor-trailer & 18-wheeler crashes",
      "Delivery & box truck accidents",
      "Jackknife & rollover collisions",
      "Underride accidents",
      "Fatigued & distracted driver crashes",
      "Improper loading & maintenance failures",
    ],
    faqs: [
      {
        q: "Who can be held liable in a truck accident?",
        a: "Liability may extend beyond the driver to the trucking company, the vehicle owner, maintenance providers, and cargo loaders. Identifying every responsible party is key to full recovery.",
      },
      {
        q: "Why are truck accident cases more complex than car accidents?",
        a: "Truck cases involve federal regulations, electronic logging data, and multiple insurers. Evidence can disappear quickly, so early investigation by experienced counsel is critical.",
      },
      {
        q: "How soon should I contact a lawyer after a truck accident?",
        a: "As soon as possible. Crucial evidence like driver logs and black-box data must be preserved before it is lost or destroyed.",
      },
    ],
    metaTitle: "Massachusetts Truck Accident Lawyer | Mahaney Law",
    metaDescription:
      "Injured in a truck or commercial vehicle crash in Massachusetts? Attorney Joseph M. Mahaney holds trucking companies accountable. 40+ years of experience. Free consultation.",
  },
  {
    slug: "slip-and-fall",
    title: "Slip and Fall Injuries",
    icon: Footprints,
    short: "Compensation for injuries caused by unsafe properties.",
    description:
      "Property owners have a duty to keep their premises reasonably safe. When they fail — through wet floors, ice, poor lighting, or hidden hazards — serious injuries follow. Attorney Mahaney pursues full compensation for slip, trip, and fall victims across Massachusetts.",
    caseTypes: [
      "Wet & slippery floors",
      "Ice & snow accumulation",
      "Broken stairs & handrails",
      "Poor lighting & hidden hazards",
      "Uneven or defective walkways",
      "Negligent property maintenance",
    ],
    faqs: [
      {
        q: "Do I have a slip and fall case?",
        a: "If a property owner's negligence caused your injury, you may have a claim. These cases turn on proving the hazard and the owner's knowledge of it — exactly what experienced counsel investigates.",
      },
      {
        q: "What if I was partly at fault?",
        a: "Massachusetts uses comparative negligence, so you may still recover even if you were partly responsible, as long as you were not more at fault than the property owner.",
      },
      {
        q: "How long do I have to file a slip and fall claim?",
        a: "Massachusetts generally allows three years from the date of injury, but evidence fades quickly, so it is best to contact an attorney promptly.",
      },
    ],
    metaTitle: "Massachusetts Slip and Fall Lawyer | Mahaney Law",
    metaDescription:
      "Hurt in a slip, trip, or fall in Massachusetts? Attorney Joseph M. Mahaney pursues full compensation from negligent property owners. Free consultation.",
  },
  {
    slug: "criminal-defense",
    title: "Criminal Defense",
    icon: Gavel,
    short: "A former prosecutor in your corner — strategy informed from the inside.",
    description:
      "Facing criminal charges can threaten your freedom, your record, and your future. As a former Assistant District Attorney with the Suffolk County DA's Office, Attorney Mahaney understands how the prosecution builds its case — and how to dismantle it.",
    caseTypes: [
      "Felony & misdemeanor charges",
      "OUI / DUI defense",
      "Drug offenses",
      "Assault & battery",
      "Theft & property crimes",
      "Probation violations & appeals",
    ],
    faqs: [
      {
        q: "Should I talk to the police before hiring a lawyer?",
        a: "No. You have the right to remain silent and the right to an attorney. Politely decline to answer questions and contact a defense attorney immediately — anything you say can be used against you.",
      },
      {
        q: "Will a criminal charge stay on my record?",
        a: "It depends on the outcome and the type of charge. In some cases, charges can be dismissed or sealed. Attorney Mahaney works to protect your record at every stage.",
      },
      {
        q: "What makes a former prosecutor a better defense attorney?",
        a: "Having prosecuted cases for the Commonwealth, Attorney Mahaney knows the strategies the other side uses and where their cases are vulnerable — insight that directly benefits your defense.",
      },
    ],
    metaTitle: "Massachusetts Criminal Defense Attorney | Mahaney Law",
    metaDescription:
      "Charged with a crime in Massachusetts? Former prosecutor Joseph M. Mahaney brings 40+ years of trial experience to your defense. Free consultation.",
  },
];

export const TRUST_BADGES = [
  { icon: Award, text: "40+ Years of Experience" },
  { icon: Building2, text: "State & Federal Trial Attorney" },
  { icon: Scale, text: "Personal Injury & Criminal Defense" },
  { icon: ShieldCheck, text: "Free Consultation" },
];

export const BADGES = [
  {
    icon: Scale,
    value: "40+",
    label: "Years of Experience",
    description: "Four decades fighting for clients throughout Massachusetts.",
  },
  {
    icon: Star,
    value: "5.0",
    label: "Client Review Rating",
    description: "Consistently praised for results, communication, and care.",
  },
  {
    icon: Landmark,
    value: "Proven",
    label: "Courtroom Experience",
    description:
      "Decades of litigation and trial experience in state and federal courts.",
  },
  {
    icon: MapPin,
    value: "Massachusetts",
    label: "Served",
    description: "Representing clients across the entire Commonwealth.",
  },
];

export const STATS = [
  {
    value: "40+",
    label: "Years of Experience",
    text: "Four decades fighting for clients throughout Massachusetts.",
  },
  {
    value: "5.0★",
    label: "Client Review Rating",
    text: "Consistently praised for results, communication, and care.",
  },
  {
    value: "All of MA",
    label: "Massachusetts Served",
    text: "Representing clients across the entire Commonwealth.",
  },
];

export const WHY_HIRE = [
  {
    icon: Award,
    title: "40+ Years of Experience",
    text: "Four decades of litigation across personal injury and criminal matters.",
  },
  {
    icon: Gavel,
    title: "Former Assistant District Attorney",
    text: "Prosecutorial insight from the Suffolk County DA's Office strengthens every case.",
  },
  {
    icon: Building2,
    title: "State & Federal Court Experience",
    text: "From Massachusetts courts to the First Circuit and the U.S. Supreme Court.",
  },
  {
    icon: ShieldCheck,
    title: "Dedicated to Client Success",
    text: "Every case is prepared for trial from day one — with direct attorney access.",
  },
];

export const CASE_RESULTS = [
  {
    value: "Multi-Million",
    label: "Dollar Settlement",
    category: "Personal Injury",
  },
  {
    value: "Six-Figure",
    label: "Injury Settlement",
    category: "Motor Vehicle Accident",
  },
  {
    value: "Acquittal",
    label: "Successful Criminal Defense",
    category: "Criminal Defense",
  },
  {
    value: "Recovery",
    label: "Slip & Fall Settlement",
    category: "Premises Liability",
  },
];

export const RESULTS_STATS = [
  { value: "40+", label: "Years in practice" },
  { value: "100s", label: "Jury trials & bench trials" },
  { value: "Fed. & State", label: "Courts of practice" },
  { value: "SCOTUS", label: "& First Circuit experience" },
];

export const EDUCATION = [
  "New England School of Law (J.D.)",
  "University of Massachusetts Amherst",
];

export const BAR_ADMISSIONS = [
  "Massachusetts Bar (1986)",
  "U.S. Federal Courts (1987)",
  "U.S. Court of Appeals, First Circuit",
  "Supreme Court of the United States",
];

export const SERVICE_AREAS = [
  "Suffolk County",
  "Middlesex County",
  "Norfolk County",
  "Essex County",
  "Worcester County",
  "Plymouth County",
  "Bristol County",
  "All of Massachusetts",
];

export const CAREER_TIMELINE = [
  {
    year: "1986",
    title: "Admitted to the Massachusetts Bar",
    body: "Began an active trial practice in state courts across the Commonwealth.",
  },
  {
    year: "1987",
    title: "Admitted to U.S. Federal Courts",
    body: "Expanded into federal litigation, later appearing before the First Circuit Court of Appeals and the U.S. Supreme Court.",
  },
  {
    year: "Early Career",
    title: "Assistant District Attorney — Suffolk County",
    body: "Served as a prosecutor with the Suffolk County District Attorney's Office, building the courtroom instincts that drive every case today.",
  },
  {
    year: "17 Years",
    title: "Partner — McLaughlin, Richards & Mahaney, LLP",
    body: "Spent 17 years as a partner handling complex civil and criminal litigation.",
  },
  {
    year: "Today",
    title: "Mahaney Law — Solo Trial Practice",
    body: "Direct, attorney-led representation focused on personal injury and criminal defense.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "I had an accident in Maine during my vacation. I tried to contact them for a solution but it seemed not to work. Then I searched online and found Joseph. This is my first time involved with a legal situation. He is an experienced personal injury lawyer and helped a lot with my case. After 4 months of negotiation, I am really satisfied with the final result. Strongly recommend.",
    name: "Verified Client",
    matter: "Personal Injury",
    footer: "TP Int • 05/03/2018",
  },
  {
    quote:
      "I have been using Joe Mahaney's legal services for years. The trust level is beyond words. Joe gets things done in a professional and timely manner. He helped me with a car accident and another personal injury case. He was kind, caring and understanding throughout the entire process. I highly recommend Joe Mahaney to anyone looking for an attorney.",
    name: "Verified Client",
    matter: "Personal Injury",
    footer: "Long-Term Client",
  },
  {
    quote:
      "Joseph Mahaney consistently receives praise for professionalism, communication, compassion, and achieving favorable outcomes for his clients.",
    name: "5.0 Overall Rating",
    matter: "32 Verified Reviews",
    footer: "TopAttorney.com",
  },
];

export const IMAGES = {
  logo: "/logo-mahaney-trim.png",
  hero_photo:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/x81r40e6_Mass%20Hero%20photo.jpg",
  parallax:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/yt7zw7ls_stock%20photo%201%20new.png",
  parallax2:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/p3k7p1hq_58a45295-08db-457a-bebc-b0847b1e4ea9.png",
  headshot:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/hpwooau2_headshot%20updated.png",
  attorney_silhouette:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/hpwooau2_headshot%20updated.png",
  office_interior:
    "https://static.prod-images.emergentagent.com/jobs/54bfdb26-e9ab-44c8-89a8-e39a71eb2fbf/images/925c5410b11faf8ca86e7f849b140af513615fb3f1bf4207891bdd3b40272d94.png",
  hero_attorney:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/2i47e01t_67D510C2-31F3-4B01-8E9C-71E2507E726A.png",
  hero_results:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/flh3fc51_4C8A0542-6EA3-4FAC-967C-F5ABB34B987C.png",
  hero_testimonials:
    "https://customer-assets.emergentagent.com/job_0dd9357b-ece2-4432-809a-8b7810b05472/artifacts/vdwjdu3r_342A0EA1-4F58-446B-90F2-57E397049442.png",
  courthouse: "https://images.pexels.com/photos/22882654/pexels-photo-22882654.jpeg",
  justice_scales: "https://images.pexels.com/photos/5669619/pexels-photo-5669619.jpeg",
};
